import { useState } from 'react';
import { CheckCircle, MessageCircle, Phone } from 'lucide-react';
import { WHATSAPP_NUMBER, WHATSAPP_URL } from '../constants';
import { useScrollReveal } from '../hooks/useScrollReveal';

const consultBenefits = [
  'Avaliação clínica completa',
  'Discussão sobre sua história com hiperidrose',
  'Explicação detalhada do procedimento e outras opções',
  'Análise de cobertura de convênio',
  'Resposta a todas as suas dúvidas',
  'Orçamento personalizado',
];

export default function ContactSection() {
  const [ref, isVisible] = useScrollReveal();
  const [form, setForm] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const parts = [`Olá, vim do site e gostaria de agendar uma consulta com dr. Mateus.`, `Meu nome é ${form.name} e meu telefone é ${form.phone}.`];
    if (form.message) parts.push(form.message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(parts.join(' '))}`, '_blank');
  };

  return (
    <section id="contato" ref={ref} className={`py-24 md:py-32 bg-gradient-to-br from-[#0D47A1] to-[#00897B] transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Info */}
          <div className="text-white">
            <div className="inline-flex items-center bg-white/15 border border-white/25 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
              Agende Sua Consulta
            </div>
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl mb-6 leading-tight">
              Seu Primeiro Passo É Uma Conversa
            </h2>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8">
              Você não precisa decidir hoje. Precisa apenas agendar uma consulta inicial.
              Conversamos sobre sua história, avaliamos clinicamente, tiramos todas as suas dúvidas,
              e você sai sabendo exatamente o que esperar.
            </p>

            <div className="space-y-3 mb-10">
              {consultBenefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-white/90 flex-shrink-0" />
                  <span className="text-white/85 text-sm md:text-base">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-primary-cta"
                className="flex items-center justify-center gap-2 bg-white text-[#0D47A1] font-poppins font-bold px-8 py-4 rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg hover:-translate-y-0.5"
              >
                <Phone size={18} />
                AGENDAR CONSULTA INICIAL
              </a>
            </div>
            <p className="text-white/55 text-xs mt-3 text-center sm:text-left">
              Agenda disponível em até 72h
            </p>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="font-poppins font-bold text-xl text-[#212121] mb-2">
              Envie sua mensagem
            </h3>
            <p className="text-[#212121]/60 text-sm mb-6">
              Preencha e falaremos com você pelo WhatsApp em breve.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                  Seu nome *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Como prefere ser chamado(a)?"
                  data-testid="contact-name-input"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                  Telefone / WhatsApp *
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  data-testid="contact-phone-input"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                  Sua dúvida ou mensagem (opcional)
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Conte um pouco sobre sua situação..."
                  data-testid="contact-message-input"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold py-4 rounded-xl hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
              >
                <MessageCircle size={18} />
                ENVIAR PELO WHATSAPP
              </button>
            </form>
            <p className="text-[#212121]/45 text-xs text-center mt-4">
              Seus dados são usados apenas para agendamento. Nunca serão compartilhados.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}
